import { APP_CONFIG } from 'src/config.app'
import { Authenticator } from 'src/services/Authenticator'
import { ApiActionNames, BearerADAxios, retryAuth } from 'src/services/AxiosInterceptor'

export type VirtualCandidateSessionStatus =
    | 'STARTING'
    | 'RUNNING'
    | 'COMPLETED'
    | 'STOPPED'
    | 'FAILED'

const ACTIVE_STATUSES: readonly VirtualCandidateSessionStatus[] = ['STARTING', 'RUNNING']

export const isActiveStatus = (status: VirtualCandidateSessionStatus): boolean =>
    ACTIVE_STATUSES.includes(status)

/** A session as the virtual candidate service describes it (GET /sessions/{id}). */
export interface VirtualCandidateSession {
    sessionId: string
    status: VirtualCandidateSessionStatus
    failureReason?: string | null
    startedAt?: string | null
    endedAt?: string | null
    /** What the session was asked to run, as the service echoes it. */
    request: {
        moduleTemplateVersionId: string
        requisitionId: string
        jobRole?: string
        voiceId: string
    }
}

export type VirtualCandidateSpeaker = 'IRENE' | 'CANDIDATE'

export interface VirtualCandidateUtterance {
    speaker: VirtualCandidateSpeaker
    phase: 'MINI_INTERVIEW' | 'INTERVIEW'
    text: string
    at: string
}

/** The practice round, or the main interview with GAS's share of competencies completed (0 to 100). */
export interface VirtualCandidateProgress {
    phase: 'MINI_INTERVIEW' | 'INTERVIEW'
    percent: number
}

export interface VirtualCandidateTranscript {
    live: boolean
    utterances: VirtualCandidateUtterance[]
    /** Who live listeners hear right now; null between turns and once the session ended. */
    speaking: VirtualCandidateSpeaker | null
    /** How far the running interview has got; null once it ended. */
    progress: VirtualCandidateProgress | null
}

/**
 * The POST /delivery-sessions fields Designer sets; the service defaults the rest. The service
 * orders the module template version from Delivery, which picks the competencies for the
 * requisition through CIS and creates the interview, then the candidate joins it.
 */
export interface StartVirtualCandidateSessionRequest {
    /** The version Delivery has: a draft's latest saved document, or a published version. */
    moduleTemplateVersionId: string
    /** Only requisitionId is required; Delivery defaults the rest of the job. */
    jobMetadata: { requisitionId: string; jobRole?: string }
    /** The candidate's system prompt; the service's built-in persona when omitted. */
    prompt?: string
    voiceId?: string
}

const baseUrl = (): string => {
    if (!APP_CONFIG.virtualCandidateBaseUrl) {
        throw new Error('The virtual candidate is not available in this stage')
    }
    return APP_CONFIG.virtualCandidateBaseUrl
}

const sessionPath = (sessionId: string) => `${baseUrl()}/sessions/${encodeURIComponent(sessionId)}`

/**
 * Client for the virtual candidate service (IScreenVirtualCandidate), which runs an AI candidate
 * through a real iScreen interview with Irene. Its load balancer and the service both check
 * Designer's Cognito ID token, which BearerADAxios attaches to every call as a Bearer token.
 */
export class VirtualCandidateService {
    static isAvailable(): boolean {
        return Boolean(APP_CONFIG.virtualCandidateBaseUrl)
    }

    static async startSession(
        request: StartVirtualCandidateSessionRequest
    ): Promise<VirtualCandidateSession> {
        const { data } = await BearerADAxios.post<VirtualCandidateSession>(
            `${baseUrl()}/delivery-sessions`,
            request,
            { apiActionName: ApiActionNames.StartVirtualCandidateSession }
        )
        return data
    }

    static async getSession(
        sessionId: string,
        signal?: AbortSignal
    ): Promise<VirtualCandidateSession> {
        const { data } = await BearerADAxios.get<VirtualCandidateSession>(sessionPath(sessionId), {
            apiActionName: ApiActionNames.GetVirtualCandidateSession,
            signal,
        })
        return data
    }

    static async getTranscript(
        sessionId: string,
        signal?: AbortSignal
    ): Promise<VirtualCandidateTranscript> {
        const { data } = await BearerADAxios.get<VirtualCandidateTranscript>(
            `${sessionPath(sessionId)}/transcript`,
            { apiActionName: ApiActionNames.GetVirtualCandidateTranscript, signal }
        )
        return data
    }

    /** Asks the interview to end early; Irene says her closing line first. */
    static async stopSession(sessionId: string): Promise<VirtualCandidateSession> {
        const { data } = await BearerADAxios.post<VirtualCandidateSession>(
            `${sessionPath(sessionId)}/stop`,
            undefined,
            { apiActionName: ApiActionNames.StopVirtualCandidateSession }
        )
        return data
    }

    /** Live audio while the session runs (WAV header, then PCM); the recording once it ended. */
    static listenUrl(sessionId: string): string {
        return `${sessionPath(sessionId)}/listen`
    }

    /** Designer's ID token, for the one call axios can't make: streaming the live audio. */
    static async getIdToken(): Promise<string> {
        const session = await retryAuth(() => Authenticator.getValidUserSession())
        return session.getIdToken().getJwtToken()
    }
}
